import Navbar from './navbar'
import React from "react";
import {Footer} from "@components/layouts/footer";
import Head from "next/head";
import {twMerge} from "tailwind-merge";
import {trackEvent} from "@utils/analytics";
import {withAssetVersion} from "@utils/assets";

export function Layout(props: {
    url: string,
    image: string,
    description: string;
    title: string;
    jsonLd?: Record<string, any> | Array<Record<string, any>> | null;
    children: React.ReactNode
}) {
    const siteName = process.env.NEXT_PUBLIC_SITE_NAME || process.env.NEXT_PUBLIC_WEBSITE_NAME || 'Plugins Database';
    const twitterHandle = process.env.NEXT_PUBLIC_TWITTER_HANDLE || '';
    const favicon16 = withAssetVersion('/images/logo/favicon-16.png');
    const favicon32 = withAssetVersion('/images/logo/favicon-32.png');
    const appleTouchIcon = withAssetVersion('/images/logo/apple-touch-icon.png');
    const manifestHref = withAssetVersion('/manifest.json');
    const metaImage = withAssetVersion(props.image);

    const normalizeBaseUrl = (value: string): string => {
        const trimmed = value.trim();
        if (!trimmed) return "";
        return trimmed.endsWith("/") ? trimmed.slice(0, -1) : trimmed;
    };

    const configuredBaseUrl = normalizeBaseUrl(
        String(process.env.NEXT_PUBLIC_WEBSITE_URL || process.env.SITE_BASE_URL || "")
    );
    const inferredBaseUrl = (() => {
        if (!props.url || typeof props.url !== "string") return "";
        try {
            const parsed = new URL(props.url);
            return normalizeBaseUrl(parsed.origin);
        } catch {
            return "";
        }
    })();
    const siteBaseUrl = configuredBaseUrl || inferredBaseUrl;

    const baseSchemas: Array<Record<string, any>> = siteBaseUrl
        ? [
            {
                "@context": "https://schema.org",
                "@type": "WebSite",
                "@id": `${siteBaseUrl}/#website`,
                "name": siteName,
                "url": siteBaseUrl,
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": {
                        "@type": "EntryPoint",
                        "urlTemplate": `${siteBaseUrl}/plugins?search={search_term_string}`,
                    },
                    "query-input": "required name=search_term_string",
                },
            },
            {
                "@context": "https://schema.org",
                "@type": "Organization",
                "@id": `${siteBaseUrl}/#organization`,
                "name": siteName,
                "url": siteBaseUrl,
                "logo": `${siteBaseUrl}${withAssetVersion('/pluginsdb-full-logo.png')}`,
            },
        ]
        : [];

    const jsonLdSchemas = [
        ...baseSchemas,
        ...(props.jsonLd
            ? (Array.isArray(props.jsonLd) ? props.jsonLd : [props.jsonLd])
            : []
        ),
    ];
    const handleLayoutClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement | null;
        trackEvent('click', {
            event_category: 'layout',
            event_label: 'click on layout',
            element: target?.tagName,
        });
    };

    return (<>
            <Head>
                <title>{`${props.title} - ${siteName}`}</title>
                <meta property="og:title" content={props.title}/>
                <link rel="canonical" href={props.url}/>
                
                {/* Favicons */}
                <link rel="icon" type="image/png" sizes="16x16" href={favicon16} />
                <link rel="icon" type="image/png" sizes="32x32" href={favicon32} />
                <link rel="apple-touch-icon" sizes="180x180" href={appleTouchIcon} />
                <link rel="manifest" href={manifestHref} />
                
                <meta name="description" content={props.description}/>
                <meta property="og:description" content={props.description}/>
                <meta property="og:image" content={metaImage}/>
                <meta property="og:url" content={props.url}/>
                <meta property="og:type" content="website"/>

                <meta name="twitter:card" content="summary_large_image"/>
                <meta name="twitter:domain" content={process.env.NEXT_PUBLIC_WEBSITE_URL}/>
                <meta name="twitter:title" content={props.title}/>
                <meta name="twitter:description" content={props.description}/>
                <meta name="twitter:site" content={twitterHandle}/>
                <meta name="twitter:creator" content={twitterHandle}/>
                <meta name="twitter:image" content={metaImage}/>
                <meta name="twitter:image:alt" content={props.title}/>
                <meta name="twitter:app:name:iphone" content={siteName}/>

                {jsonLdSchemas.map((schema, index) => (
                    <script
                        key={`jsonld-${index}`}
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(schema).replace(/</g, '\\u003c'),
                        }}
                    />
                ))}
            </Head>
            <div className="min-h-screen
                            flex flex-col
                            bg-background text-foreground"
                 tabIndex={0}
                 onClick={handleLayoutClick}
            >

                <Navbar className="flex-none"/>
                <main
                    id="app-body"
                    className={twMerge(
                        "flex-1",
                        "flex flex-col",
                        "scroll-smooth"
                    )}>
                    {props.children}
                </main>
                <Footer className="flex-none"/>
            </div>
        </>
    )
}
